import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Link,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { orderService } from "../../services/orderService"; // Adjust path if needed
import { format } from "date-fns";
import useDocumentTitle from "../../hooks/useDocumentTitle"; // Adjust path if needed
import { getStatusChipColor } from "../../utils/statusUtils"; // Import the utility function

const AdminOrdersPage = () => {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("orders");

  useDocumentTitle(t("admin.list.title"));
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalOrders, setTotalOrders] = useState(0);
  const [filters, setFilters] = useState({ status: "", search: "" });
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch orders based on pagination and filters
  useEffect(() => {
    const fetchAdminOrders = async () => {
      setLoading(true);
      setError(null);
      try {
        // Pass individual arguments to the service function
        const data = await orderService.getAdminOrders(
          page + 1, // API uses 1-based pages
          rowsPerPage,
          filters.status,
          "", // Remove userId filter - passing empty string
          filters.search
        );
        setOrders(data.orders || []);
        setTotalOrders(data.pagination?.total || 0);
      } catch (err) {
        setError(err.message || tCommon("status.error"));
        console.error("Fetch admin orders error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAdminOrders();
  }, [page, rowsPerPage, filters, tCommon]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(0); // Reset page when filters change
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setFilters((prev) => ({ ...prev, search: searchTerm }));
    setPage(0); // Reset to first page when search changes
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilters((prev) => ({ ...prev, search: "" }));
  };

  // Define possible statuses for filter dropdown using translations
  const orderStatuses = [
    { value: "", label: t("admin.list.filters.allStatuses") },
    { value: "pending", label: t("shared.status.pending") },
    { value: "awaiting_payment", label: t("shared.status.awaiting_payment") },
    { value: "processing", label: t("shared.status.processing") },
    { value: "shipped", label: t("shared.status.shipped") },
    { value: "delivered", label: t("shared.status.delivered") },
    { value: "return_requested", label: t("shared.status.return_requested") },
    { value: "return_processing", label: t("shared.status.return_processing") },
    { value: "return_approved", label: t("shared.status.return_approved") },
    { value: "return_rejected", label: t("shared.status.return_rejected") },
    { value: "returned", label: t("shared.status.returned") },
    { value: "canceled", label: t("shared.status.canceled") },
    { value: "failed", label: t("shared.status.failed") },
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {" "}
      {/* Use wider container for admin table */}
      <Typography variant="h4" component="h1" gutterBottom>
        {t("admin.list.title")}
      </Typography>
      {/* Filters Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>{t("admin.list.filters.status")}</InputLabel>
            <Select
              label={t("admin.list.filters.status")}
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
            >
              {orderStatuses.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Search functionality */}
          <form
            onSubmit={handleSearchSubmit}
            style={{ display: "flex", flexGrow: 1 }}
          >
            <TextField
              label={t("admin.list.filters.search")}
              value={searchTerm}
              onChange={handleSearchChange}
              size="small"
              variant="outlined"
              placeholder={t("admin.list.filters.searchPlaceholder")}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {searchTerm && (
                      <IconButton
                        onClick={handleClearSearch}
                        aria-label="clear search"
                        edge="end"
                        size="small"
                      >
                        ×
                      </IconButton>
                    )}
                    <IconButton type="submit" aria-label="search" edge="end">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </form>
        </Box>

        {/* Show active filters */}
        {(filters.status || filters.search) && (
          <Box sx={{ mt: 2, display: "flex", gap: 1, alignItems: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Active filters:
            </Typography>
            {filters.status && (
              <Chip
                size="small"
                label={`Status: ${
                  orderStatuses.find((s) => s.value === filters.status)
                    ?.label || filters.status
                }`}
                onDelete={() => setFilters((prev) => ({ ...prev, status: "" }))}
              />
            )}
            {filters.search && (
              <Chip
                size="small"
                label={`Search: ${filters.search}`}
                onDelete={() => setFilters((prev) => ({ ...prev, search: "" }))}
              />
            )}
          </Box>
        )}
      </Paper>
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
          <CircularProgress />
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      )}
      {!loading && !error && orders.length === 0 && (
        <Typography sx={{ mt: 3 }}>{t("admin.list.noOrders")}</Typography>
      )}
      {!loading && !error && orders.length > 0 && (
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer>
            <Table stickyHeader aria-label="admin orders table">
              <TableHead>
                <TableRow>
                  <TableCell>{t("admin.list.columns.orderNumber")}</TableCell>
                  <TableCell>{t("admin.list.columns.date")}</TableCell>
                  <TableCell>{t("admin.list.columns.customer")}</TableCell>
                  <TableCell align="right">
                    {t("admin.list.columns.total")}
                  </TableCell>
                  <TableCell>{t("admin.list.columns.status")}</TableCell>
                  <TableCell>{t("admin.list.columns.paymentStatus")}</TableCell>
                  <TableCell>{t("admin.list.columns.paymentMethod")}</TableCell>
                  <TableCell align="center">
                    {t("admin.list.columns.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                  <TableRow hover role="checkbox" tabIndex={-1} key={order.id}>
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/admin/orders/${order.id}`}
                      >
                        {order.orderNumber}
                      </Link>
                    </TableCell>
                    <TableCell>
                      {format(new Date(order.createdAt), "MMM d, yyyy h:mm a")}
                    </TableCell>
                    <TableCell>
                      {/* Display companyName or email as primary identifier */}
                      {order.user ? (
                        <>
                          {order.user.companyName || order.user.email}
                          <Typography variant="caption" display="block">
                            {/* Show email if companyName was displayed, or nothing */}
                            {order.user.companyName ? order.user.email : ""}
                          </Typography>
                        </>
                      ) : (
                        tCommon("status.notApplicable")
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {/* Parse total string to float */}
                      {`${
                        typeof order.total === "number" ||
                        (typeof order.total === "string" &&
                          !isNaN(parseFloat(order.total)))
                          ? parseFloat(order.total).toFixed(2)
                          : tCommon("status.notApplicable")
                      } CHF`}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(
                          `shared.status.${order.status?.toLowerCase()}`,
                          order.status || t("shared.status.unknown")
                        )}
                        color={getStatusChipColor(order.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(
                          `shared.status.${order.paymentStatus?.toLowerCase()}`,
                          order.paymentStatus || t("shared.status.unknown")
                        )}
                        color={getStatusChipColor(order.paymentStatus)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{order.paymentMethod}</TableCell>
                    <TableCell align="center">
                      <Button
                        component={RouterLink}
                        to={`/admin/orders/${order.id}`}
                        size="small"
                        variant="outlined"
                      >
                        {t("admin.list.columns.viewDetails")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalOrders}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={tCommon("pagination.rowsPerPage")}
          />
        </Paper>
      )}
    </Container>
  );
};

export default AdminOrdersPage;
